/**
 * DevRequestPanel - Floating debug panel for request monitoring
 * 
 * Shows last 50 requests with:
 * - Endpoint name
 * - Duration (ms)
 * - Status (ok/error)
 * - Trace ID
 * - Timestamp
 * 
 * Also shows recent assertions.
 * Only rendered in development mode.
 */
import { useState, useEffect, useCallback } from 'react';
import { X, ChevronDown, ChevronUp, Bug, Zap, AlertTriangle, CheckCircle, Copy, Trash2 } from 'lucide-react';
import { getRecentRequests, type RequestLogEntry } from '@/lib/fetchTrace';
import { getRecentAssertions, type AssertionLogEntry } from '@/dev/devAssertions';
import { getCacheStats } from '@/services/rpcClient';
import { cn } from '@/lib/utils';

// Only show in DEV
const IS_DEV = import.meta.env.DEV;

type TabType = 'requests' | 'assertions' | 'stats';

function DevRequestPanelContent() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('requests');
  const [requests, setRequests] = useState<RequestLogEntry[]>([]);
  const [assertions, setAssertions] = useState<AssertionLogEntry[]>([]);
  const [filter, setFilter] = useState<'all' | 'errors' | 'slow'>('all');
  
  // Listen for new requests and assertions
  useEffect(() => {
    const handleRequest = () => {
      setRequests(getRecentRequests());
    };
    
    const handleAssertion = () => {
      setAssertions(getRecentAssertions());
    };
    
    // Initial load
    setRequests(getRecentRequests());
    setAssertions(getRecentAssertions());
    
    window.addEventListener('fw:request', handleRequest);
    window.addEventListener('fw:assertion', handleAssertion);
    
    // Periodic refresh for stats
    const interval = setInterval(() => {
      if (isOpen && activeTab === 'stats') {
        // Force re-render for stats
        setRequests(getRecentRequests());
      }
    }, 1000);
    
    return () => {
      window.removeEventListener('fw:request', handleRequest);
      window.removeEventListener('fw:assertion', handleAssertion);
      clearInterval(interval);
    };
  }, [isOpen, activeTab]);
  
  const filteredRequests = requests.filter(r => {
    if (filter === 'errors') return r.status === 'error';
    if (filter === 'slow') return r.durationMs > 500;
    return true;
  });
  
  const errorCount = requests.filter(r => r.status === 'error').length;
  const assertionCount = assertions.filter(a => a.level === 'fatal').length;
  
  const copyToClipboard = useCallback(() => {
    const data = {
      requests: filteredRequests.slice(0, 20),
      assertions: assertions.slice(0, 10),
      stats: getCacheStats(),
      timestamp: new Date().toISOString()
    };
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
  }, [filteredRequests, assertions]);
  
  const clearLogs = useCallback(() => {
    const g = globalThis as any;
    if (g.__fw_request_log) g.__fw_request_log.length = 0;
    if (g.__fw_assertion_log) g.__fw_assertion_log.length = 0;
    setRequests([]);
    setAssertions([]);
  }, []);
  
  const cacheStats = getCacheStats();
  
  // Floating button when closed
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed bottom-4 right-4 z-50 p-3 rounded-full shadow-lg transition-all",
          "bg-gray-800 hover:bg-gray-700 text-white",
          (errorCount > 0 || assertionCount > 0) && "bg-red-600 hover:bg-red-500 animate-pulse"
        )}
        title="Open DevRequestPanel"
      >
        <Bug className="h-5 w-5" />
        {(errorCount > 0 || assertionCount > 0) && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {errorCount + assertionCount}
          </span>
        )}
      </button>
    );
  }
  
  return (
    <div 
      className={cn(
        "fixed bottom-4 right-4 z-50 bg-gray-900 text-white rounded-lg shadow-2xl border border-gray-700",
        "transition-all duration-200",
        isMinimized ? "w-80" : "w-[600px]"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <Bug className="h-4 w-4 text-blue-400" />
          <span className="font-semibold text-sm">DevRequestPanel</span>
          {errorCount > 0 && (
            <span className="bg-red-500 text-xs px-2 py-0.5 rounded-full">{errorCount} errors</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={copyToClipboard} className="p-1 hover:bg-gray-700 rounded" title="Copy logs">
            <Copy className="h-4 w-4" />
          </button>
          <button onClick={clearLogs} className="p-1 hover:bg-gray-700 rounded" title="Clear logs">
            <Trash2 className="h-4 w-4" />
          </button>
          <button onClick={() => setIsMinimized(!isMinimized)} className="p-1 hover:bg-gray-700 rounded">
            {isMinimized ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-gray-700 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      {!isMinimized && (
        <>
          {/* Tabs */}
          <div className="flex border-b border-gray-700">
            <button
              onClick={() => setActiveTab('requests')}
              className={cn(
                "flex-1 py-2 text-xs font-medium",
                activeTab === 'requests' ? "bg-gray-800 text-white" : "text-gray-400 hover:text-white"
              )}
            >
              Requests ({requests.length})
            </button>
            <button
              onClick={() => setActiveTab('assertions')}
              className={cn(
                "flex-1 py-2 text-xs font-medium",
                activeTab === 'assertions' ? "bg-gray-800 text-white" : "text-gray-400 hover:text-white",
                assertionCount > 0 && "text-red-400"
              )}
            >
              Assertions ({assertions.length})
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={cn(
                "flex-1 py-2 text-xs font-medium",
                activeTab === 'stats' ? "bg-gray-800 text-white" : "text-gray-400 hover:text-white"
              )}
            >
              Stats
            </button>
          </div>
          
          {/* Content */}
          <div className="max-h-80 overflow-y-auto p-2">
            {activeTab === 'requests' && (
              <>
                {/* Filter */}
                <div className="flex gap-1 mb-2">
                  {(['all', 'errors', 'slow'] as const).map(f => (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className={cn(
                        "px-2 py-1 text-xs rounded",
                        filter === f ? "bg-blue-600" : "bg-gray-700 hover:bg-gray-600"
                      )}
                    >
                      {f}
                    </button>
                  ))}
                </div>
                
                {/* Request list */}
                <div className="space-y-1">
                  {filteredRequests.length === 0 ? (
                    <p className="text-gray-500 text-xs text-center py-4">No requests yet</p>
                  ) : (
                    filteredRequests.map(req => (
                      <div
                        key={req.id}
                        className={cn(
                          "p-2 rounded text-xs font-mono",
                          req.status === 'error' ? "bg-red-900/30" : 
                          req.status === 'pending' ? "bg-yellow-900/30" : 
                          req.durationMs > 500 ? "bg-orange-900/30" : "bg-gray-800"
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            {req.status === 'ok' && <CheckCircle className="h-3 w-3 text-green-400 flex-shrink-0" />}
                            {req.status === 'error' && <AlertTriangle className="h-3 w-3 text-red-400 flex-shrink-0" />}
                            {req.status === 'pending' && <Zap className="h-3 w-3 text-yellow-400 flex-shrink-0" />}
                            <span className="truncate">{req.endpoint}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={cn(
                              req.durationMs > 500 ? "text-orange-400" : "text-gray-400"
                            )}>
                              {req.durationMs}ms
                            </span>
                          </div>
                        </div>
                        {req.traceId && (
                          <div className="text-gray-500 mt-1 truncate">
                            trace: {req.traceId}
                          </div>
                        )}
                        {req.error && (
                          <div className="text-red-400 mt-1 truncate">
                            {req.error}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
            
            {activeTab === 'assertions' && (
              <div className="space-y-1">
                {assertions.length === 0 ? (
                  <p className="text-gray-500 text-xs text-center py-4">No assertions triggered</p>
                ) : (
                  assertions.map(a => (
                    <div
                      key={a.id}
                      className={cn(
                        "p-2 rounded text-xs",
                        a.level === 'fatal' ? "bg-red-900/30" : "bg-yellow-900/30"
                      )}
                    >
                      <div className="flex items-center gap-1.5">
                        {a.level === 'fatal' ? (
                          <AlertTriangle className="h-3 w-3 text-red-400" />
                        ) : (
                          <AlertTriangle className="h-3 w-3 text-yellow-400" />
                        )}
                        <span className="font-medium">{a.name}</span>
                      </div>
                      <div className="text-gray-300 mt-1">{a.message}</div>
                      {a.context && (
                        <div className="text-gray-500 mt-1 font-mono text-[10px]">
                          {JSON.stringify(a.context)}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
            
            {activeTab === 'stats' && (
              <div className="space-y-3 text-xs">
                <div className="bg-gray-800 p-3 rounded">
                  <h4 className="font-medium mb-2">Cache</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>Entries: <span className="text-blue-400">{cacheStats.size}</span></div>
                    <div>In-flight: <span className="text-yellow-400">{cacheStats.inFlightCount}</span></div>
                  </div>
                </div>
                
                <div className="bg-gray-800 p-3 rounded">
                  <h4 className="font-medium mb-2">Circuit Breakers</h4>
                  {Object.keys(cacheStats.circuitBreakers).length === 0 ? (
                    <span className="text-gray-500">All circuits closed</span>
                  ) : (
                    <div className="space-y-1">
                      {Object.entries(cacheStats.circuitBreakers).map(([key, state]) => (
                        <div key={key} className="flex justify-between">
                          <span className="truncate">{key}</span>
                          <span className={cn(
                            state === 'open' ? "text-red-400" : 
                            state === 'half-open' ? "text-yellow-400" : "text-green-400"
                          )}>
                            {state}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="bg-gray-800 p-3 rounded">
                  <h4 className="font-medium mb-2">Blacklisted RPCs (no cache)</h4>
                  <div className="text-gray-400">
                    {cacheStats.blacklist.join(', ')}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      
      {/* Minimized summary */}
      {isMinimized && (
        <div className="p-2 text-xs text-gray-400">
          {requests.length} requests | {errorCount} errors | {cacheStats.size} cached
        </div>
      )}
    </div>
  );
}

// Export wrapper that handles DEV check at the top level
export function DevRequestPanel() {
  // Check DEV mode before any hooks
  if (!IS_DEV) return null;
  
  return <DevRequestPanelContent />;
}
